import { StoreProvider, useStoreState } from 'easy-peasy';
import * as R from 'ramda';
import React, { useEffect, useState, useMemo } from 'react';
import { Router } from 'react-router-dom';
import { history } from '~/history';
import { Routes } from '~/Routes';
import { store } from '~/store';
import { ApiContext } from './context';
import { api } from './api';
import Tracking from './tracking';
import Helmet from 'Components/Helmet';
import { ToastContainer, Slide } from 'react-toastify';
import Empty from 'Components/Empty';
import { IntlProvider } from 'react-intl';
import { LANGUAGES, useLocaleProvider } from './lang';
import Intercom from 'Components/Intercom';
import InactiveAccountPage from 'Pages/InactiveAccount';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import relativeTime from 'dayjs/plugin/relativeTime';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import duration from 'dayjs/plugin/duration';
import isMoment from 'dayjs/plugin/isMoment';
import localizedFormat from 'dayjs/plugin/localizedFormat';

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.extend(relativeTime);
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(duration);
dayjs.extend(isMoment);
dayjs.extend(localizedFormat);

const project = spec => rootCss => {
  if (!rootCss) return {};

  const newkeys = Object.keys(spec);

  const ret = {};

  newkeys.forEach(key => {
    spec[key].forEach(oldKey => {
      Object.assign(
        ret,
        R.assoc(oldKey, R.propOr(R.prop(oldKey, rootCss), key, rootCss), ret)
      );
    });
  });

  return ret;
};

const generateCss = project({
  fraiche_primary: [
    'primary-color',
    'loader',
    'navbar',
    'checkbox',
    'radio',
    'highlight',
  ],
  fraiche_primary_2: ['mobile-nav-background'],
  fraiche_secondary: [
    'secondary-color',
    'loader-before',
    'link',
    'place-new-order',
  ],
  fraiche_tertiary: ['loader-after'],
  gray_1: ['panel-h2'],
  gray_2: ['main-panel'],
  gray_3: ['light-panel'],
});

function App() {
  // react-router runs a bit delay when using useStoreState and store gets updated (in PrivateRoute)
  //  Thus get user here and pass it into Routes.
  // TODO If there are more usages, try to pass in user through context,
  //  for components cannot properly react to store update.
  const { loading, user, solution } = useStoreState(state =>
    R.pickAll(['loading', 'user', 'solution'], state.auth)
  );

  const rootCss = {
    fraiche_primary: '#162f53',
    fraiche_primary_2: '#367bc2',
    fraiche_secondary: '#1ba9d6',
    fraiche_tertiary: '#eb3b2b',
    gray_1: '#666666',
    gray_2: '#999999',
    gray_3: '#f2f2f2',
    ...R.path(['ui_settings', 'lite'], solution),
  };

  const [favicon, setFavicon] = useState();

  useEffect(() => {
    if (solution && solution.favicon) {
      setFavicon(solution.favicon);
    } else {
      setFavicon('/favicon.ico');
    }
  }, [solution]);

  const { messages, locale } = useLocaleProvider();

  // Convert app locale codes to proper react-intl BCP 47 format
  const reactIntlLocale = LANGUAGES[locale]?.reactIntlLocale || 'en-US';

  useEffect(() => {
    if (!locale || locale === 'en') {
      return;
    }
    (async () => {
      let thisLocale = locale;
      if (locale === 'zh') {
        thisLocale = 'cn';
      }
      await LANGUAGES[thisLocale].dayjsLocaleLoader();
      dayjs.locale(LANGUAGES[thisLocale].dayjsLocaleId || thisLocale);
    })();
  }, [locale]);

  useEffect(() => {
    if (document.body.hasAttribute('data-localization-ready')) {
      return;
    }
    if (messages && Object.keys(messages).length > 0) {
      document.body.setAttribute('data-localization-ready', 'true');
    }
  }, [messages]);

  const newCss = useMemo(() => generateCss(rootCss), [rootCss]);

  const mapCss = `:root {
  ${Object.entries(newCss)
    .map(([key, value]) => `--${key}: ${value};`)
    .join('\n')}
  }`;

  if (!loading && !solution && user) {
    return <InactiveAccountPage />;
  }

  return (
    <IntlProvider
      messages={messages}
      locale={reactIntlLocale}
      defaultLocale="en-US"
      onError={err => {
        if (err.code === 'MISSING_TRANSLATION') return;
        if (err.code === 'MISSING_DATA') return;
        console.error(err);
      }}
    >
      {favicon && (
        <Helmet>
          <link rel="shortcut icon" href={favicon} />
        </Helmet>
      )}
      {rootCss && (
        <Helmet>
          <style>{mapCss}</style>
        </Helmet>
      )}
      <ApiContext.Provider value={api}>
        <Router history={history}>
          <Routes solution={solution} user={user} />
          <Tracking />
          <Empty />
        </Router>
      </ApiContext.Provider>
      <ToastContainer
        position="bottom-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        transition={Slide}
        type="info"
      />
      {import.meta.env.VITE_APP_INTERCOM_APP_ID && <Intercom />}
    </IntlProvider>
  );
}

export default () => (
  <StoreProvider store={store}>
    <App />
  </StoreProvider>
);
